import React, { useEffect } from 'react';
import styled from 'styled-components';
import Flex from '../Flex';
import Text from '../Text';
import CloseButton from '../CloseButton';

interface RightDrawerProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    width?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    closeOnBackdrop?: boolean;
}

const RightDrawer = ({
    open,
    onClose,
    title,
    width = '420px',
    children,
    footer,
    closeOnBackdrop = true,
}: RightDrawerProps) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    return (
        <Wrapper $open={open} aria-hidden={!open}>
            <Backdrop $open={open} onClick={() => closeOnBackdrop && onClose()} />
            <Panel $open={open} $width={width} role="dialog" aria-modal="true">
                <Header align="center" justify="between" gap="0.5rem">
                    {title ? <Text fontSize={18} bold margin="0">{title}</Text> : <span />}
                    <CloseButton onClose={onClose} shadow={false} />
                </Header>
                <Content column $grow={1}>{children}</Content>
                {footer && <Footer>{footer}</Footer>}
            </Panel>
        </Wrapper>
    );
};

export default RightDrawer;

const Wrapper = styled.div<{ $open: boolean }>`
    position: fixed;
    inset: 0;
    z-index: 1100;
    pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

const Backdrop = styled.div<{ $open: boolean }>`
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.35);
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transition: opacity 0.2s ease;
`;

const Panel = styled.div<{ $open: boolean; $width: string }>`
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: ${({ $width }) => $width};
    max-width: 100vw;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
    transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
    transition: transform 0.2s ease;
`;

const Header = styled(Flex)`
    padding: 1rem 1.25rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    flex-shrink: 0;
`;

const Content = styled(Flex)`
    padding: 1rem 1.25rem;
    overflow-y: auto;
    flex: 1;
`;

const Footer = styled.div`
    padding: 0.75rem 1.25rem;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    background-color: ${({ theme }) => theme.colors.fillWhite};
    flex-shrink: 0;
`;
